import React, { useState } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import { 
    FaHeart, FaHandHoldingHeart, FaGraduationCap, FaPhone, FaEnvelope, 
    FaMapMarkerAlt, FaUniversity, FaMoneyBillWave, FaCreditCard, FaShieldAlt, 
    FaUsers, FaChild, FaLock, FaCheckCircle, FaTimes, FaQrcode, FaMobile,
    FaWallet, FaGooglePay, FaPaypal, FaDownload, FaShare, FaStar,
    FaCalendarAlt, FaReceipt, FaGift, FaChartLine, FaUserShield
} from 'react-icons/fa';
import phonepeDirectService from '../services/phonepeDirectService';

// Check PhonePe configuration
const phonepeConfig = phonepeDirectService.checkPhonePeConfiguration();

// Animation variants
const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.15,
            delayChildren: 0.1
        }
    }
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.5, ease: "easeOut" }
    }
};

const slideIn = {
    hidden: { opacity: 0, x: -50 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.5, ease: "easeOut" }
    }
};

const DonationPage = () => {
    // State management for donation form
    const [donationAmount, setDonationAmount] = useState('');
    const [customAmount, setCustomAmount] = useState('');
    const [donationFrequency, setDonationFrequency] = useState('one-time');
    const [selectedCause, setSelectedCause] = useState('');

    const [paymentMethod, setPaymentMethod] = useState('phonepe');
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showPhonePeIframe, setShowPhonePeIframe] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [errorMessage, setErrorMessage] = useState('');
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [donorInfo, setDonorInfo] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        panNumber: '',
        needTaxCertificate: false
    });

    // Preset donation amounts
    const presetAmounts = [500, 1000, 2500, 5000, 10000];

    // Donation causes
    const donationCauses = [
        { value: 'education', label: 'Education & Literacy Programs' },
        { value: 'health', label: 'Healthcare & Medical Support' },
        { value: 'nutrition', label: 'Nutrition & Food Security' },
        { value: 'shelter', label: 'Shelter & Housing' },
        { value: 'emergency', label: 'Emergency Relief' },
        { value: 'general', label: 'General Fund (Where Most Needed)' }
    ];

    // Payment methods - Only PhonePe
    const paymentMethods = [
        { id: 'phonepe', name: 'Pay via PhonePe', icon: <FaMobile />, description: 'UPI, Cards, Wallets & More', color: 'text-purple-600' }
    ];

    /*
    // Recent donors (mock data)
    const recentDonors = [
        { name: 'Rajesh K.', amount: 5000, cause: 'Education', time: '2 hours ago' },
        { name: 'Priya S.', amount: 2500, cause: 'Healthcare', time: '4 hours ago' },
        { name: 'Anonymous', amount: 10000, cause: 'General Fund', time: '6 hours ago' },
        { name: 'Amit P.', amount: 1000, cause: 'Nutrition', time: '8 hours ago' }
    ];
    */

    // Testimonials
    const testimonials = [
        {
            name: 'Sunita Devi',
            role: 'Beneficiary Mother',
            message: 'Thanks to your support, my daughter can now attend school and has a bright future ahead.',
            rating: 5
        },
        {
            name: 'Ravi Kumar',
            role: 'Community Leader',
            message: 'The healthcare programs have transformed our village. Children are healthier and happier.',
            rating: 5
        }
    ];



    const donationImpacts = [

        {
            amount: '₹1000',
            description: 'Can support a family with essential nutritional supplies',
            icon: <FaHeart />,
            color: 'red'
        },
        {
            amount: '₹2500',
            description: 'Can contribute to a child\'s school fees and uniform',
            icon: <FaChild />,
            color: 'green'
        },
        {
            amount: '₹5000',
            description: 'Can help fund a community health awareness workshop',
            icon: <FaUsers />,
            color: 'purple'
        }
    ];

    /**
     * Calculate the impact of the current donation amount
     */
    const calculateImpact = (amount) => {
        const impacts = [];
        const numAmount = parseInt(amount) || 0;
        

        if (numAmount >= 1000) {
            const families = Math.floor(numAmount / 1000);
            impacts.push(`Support ${families} famil${families > 1 ? 'ies' : 'y'} with nutritional supplies`);
        }
        if (numAmount >= 2500) {
            const children = Math.floor(numAmount / 2500);
            impacts.push(`Cover school fees for ${children} child${children > 1 ? 'ren' : ''}`);
        }
        
        return impacts;
    };

    /**
     * Validate form fields
     */
    const validateForm = () => {
        const errors = {};
        
        // Validate amount
        const amount = getFinalAmount();
        if (!amount || amount <= 0) {
            errors.amount = 'Please select or enter a donation amount';
        } else if (amount < 100) {
            errors.amount = 'Minimum donation amount is ₹100';
        } else if (amount > 100000) {
            errors.amount = 'Maximum donation amount is ₹1,00,000';
        }
        
        // Validate name
        if (!donorInfo.name.trim()) {
            errors.name = 'Full name is required';
        } else if (donorInfo.name.trim().length < 3) {
            errors.name = 'Name must be at least 3 characters';
        } else if (!/^[a-zA-Z\s]+$/.test(donorInfo.name)) {
            errors.name = 'Name should contain only letters';
        }
        
        // Validate email
        if (!donorInfo.email.trim()) {
            errors.email = 'Email address is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(donorInfo.email)) {
            errors.email = 'Please enter a valid email address';
        }
        
        // Validate phone
        if (!donorInfo.phone.trim()) {
            errors.phone = 'Phone number is required';
        } else {
            const cleanPhone = donorInfo.phone.replace(/\D/g, '');
            
            // Handle different phone number formats
            if (cleanPhone.length === 10) {
                // Standard 10-digit number
                if (!/^[6-9]/.test(cleanPhone)) {
                    errors.phone = 'Please enter a valid Indian mobile number (should start with 6, 7, 8, or 9)';
                }
            } else if (cleanPhone.length === 11 && cleanPhone.startsWith('0')) {
                // 11-digit number starting with 0 (like 0123456789)
                const withoutZero = cleanPhone.substring(1);
                if (!/^[6-9]/.test(withoutZero)) {
                    errors.phone = 'Please enter a valid Indian mobile number (should start with 6, 7, 8, or 9 after 0)';
                }
            } else if (cleanPhone.length === 12 && cleanPhone.startsWith('91')) {
                // 12-digit number with country code (like 919876543210)
                const withoutCountryCode = cleanPhone.substring(2);
                if (!/^[6-9]/.test(withoutCountryCode)) {
                    errors.phone = 'Please enter a valid Indian mobile number (should start with 6, 7, 8, or 9)';
                }
            } else {
                errors.phone = 'Please enter a valid 10-digit Indian mobile number';
            }
        }
        
        // Validate PAN if tax certificate is needed
        if (donorInfo.needTaxCertificate) {
            if (!donorInfo.panNumber.trim()) {
                errors.panNumber = 'PAN number is required for tax certificate';
            } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(donorInfo.panNumber)) {
                errors.panNumber = 'Please enter a valid PAN number (e.g., ABCDE1234F)';
            }
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    /**
     * Handle donation amount selection
     */
    const handleAmountSelect = (amount) => {
        setDonationAmount(amount);
        setCustomAmount('');
    };

    /**
     * Handle custom amount input
     */
    const handleCustomAmountChange = (e) => {
        const value = e.target.value;
        setCustomAmount(value);
        if (value) {
            setDonationAmount('');
        }
    };

    /**
     * Handle donor information change
     */
    const handleDonorInfoChange = (field, value) => {
        setDonorInfo(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear specific field error when user starts typing
        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    /**
     * Handle PhonePe payment processing with industry-standard workflow
     * Implements proper error handling, user feedback, and payment flow
     */
    const handlePhonePePayment = async () => {
        try {
            console.log('[DonationPage] Initiating PhonePe payment', { method: paymentMethod });
            setIsProcessing(true);
            
            // Check PhonePe configuration
            if (!phonepeConfig.isValid) {
                throw new Error(phonepeConfig.errorMessage);
            }
            
            const amount = getFinalAmount();
            const userId = `USER_${Date.now()}`;
            
            // Validate amount
            if (!amount || amount <= 0) {
                throw new Error('Please enter a valid donation amount');
            }

            // Store donation details for success page
            const donationDetails = {
                amount: parseInt(amount),
                donorName: donorInfo.name,
                donorEmail: donorInfo.email,
                donorPhone: donorInfo.phone,
                cause: selectedCause,
                frequency: donationFrequency,
                timestamp: new Date().toISOString()
            };
            
            // Store in localStorage for success page
            localStorage.setItem('donation_details', JSON.stringify(donationDetails));
            
            // Prepare PhonePe payment payload
            const paymentData = {
                userId: userId,
                amount: parseInt(amount),
                phone: donorInfo.phone,
                transactionId: `TXN_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
                donorInfo: {
                    name: donorInfo.name,
                    email: donorInfo.email,
                    cause: selectedCause,
                    frequency: donationFrequency
                }
            };

            console.log('[DonationPage] Initiating PhonePe donation flow (Direct)', paymentData);
            
            // Call PhonePe direct service to initiate complete donation flow
            // This will generate auth token and create payment
            const result = await phonepeDirectService.initiateDonation(paymentData);
            console.log('[DonationPage] PhonePe donation flow response', result);

            if (result.success) {
                // Extract redirect URL from response (v2 API format)
                const tokenUrl = result?.redirectUrl;

                if (tokenUrl) {
                    console.log('[DonationPage] Opening PhonePe payment page in iframe:', tokenUrl);
                    console.log('[DonationPage] Order details:', {
                        orderId: result.orderId,
                        state: result.state,
                        merchantOrderId: result.merchantOrderId,
                        amount: result.amount
                    });
                    
                    // Open PhonePe PayPage in iframe mode
                    openPhonePeIframe(tokenUrl, result.merchantOrderId);
                } else {
                    throw new Error('Payment redirect URL not received from PhonePe');
                }
            } else {
                throw new Error(result.message || 'Payment initialization failed');
            }
            
        } catch (error) {
            console.error('[DonationPage] PhonePe payment error:', error);
            setIsProcessing(false);
            setShowPaymentModal(false);
            
            // Show error modal with detailed message
            setErrorMessage(error.message || 'Payment failed. Please try again.');
            setShowErrorModal(true);
        }
    };

    // PhonePe iframe integration functions
    const openPhonePeIframe = (tokenUrl, merchantOrderId) => {
        try {
            console.log('[DonationPage] Opening PhonePe iframe with tokenUrl:', tokenUrl);
            
            // Check if PhonePe Checkout script is loaded
            if (!window.PhonePeCheckout || !window.PhonePeCheckout.transact) {
                throw new Error('PhonePe Checkout script not loaded. Please refresh the page and try again.');
            }

            // Show iframe overlay
            setShowPhonePeIframe(true);
            setIsProcessing(false);
            setShowPaymentModal(false);

            // Define callback function for payment completion
            const paymentCallback = (response) => {
                console.log('[DonationPage] PhonePe payment callback received:', response);
                
                if (response === 'USER_CANCEL') {
                    console.log('[DonationPage] User cancelled the payment');
                    setShowPhonePeIframe(false);
                    setErrorMessage('Payment was cancelled. You can try again anytime.');
                    setShowErrorModal(true);
                } else if (response === 'CONCLUDED') {
                    console.log('[DonationPage] Payment concluded, checking status...');
                    setShowPhonePeIframe(false);
                    
                    // Check payment status after a short delay
                    setTimeout(() => {
                        checkPaymentStatus(merchantOrderId);
                    }, 2000);
                }
            };

            // Open PhonePe PayPage in iframe mode
            window.PhonePeCheckout.transact({
                tokenUrl: tokenUrl,
                callback: paymentCallback,
                type: "IFRAME"
            });

        } catch (error) {
            console.error('[DonationPage] Error opening PhonePe iframe:', error);
            setShowPhonePeIframe(false);
            setIsProcessing(false);
            setErrorMessage(error.message || 'Failed to open payment page. Please try again.');
            setShowErrorModal(true);
        }
    };

    const checkPaymentStatus = async (merchantOrderId) => {
        try {
            console.log('[DonationPage] Checking payment status for:', merchantOrderId);
            
            // Use the direct service to check payment status with detailed information (v2 API format)
            const statusResult = await phonepeDirectService.checkPaymentStatus(merchantOrderId, true, true);
            console.log('[DonationPage] Payment status result:', statusResult);

            if (statusResult.success) {
                const status = statusResult.state;
                console.log('[DonationPage] Payment status:', status);
                console.log('[DonationPage] Payment details:', {
                    orderId: statusResult.orderId,
                    state: statusResult.state,
                    amount: statusResult.amount,
                    paymentAttempts: statusResult.paymentDetails?.length || 0,
                    latestPayment: statusResult.latestPayment
                });

                if (statusResult.isCompleted) {
                    // Payment successful
                    console.log('[DonationPage] Payment completed successfully!');
                    
                    // Store payment details for success page
                    const paymentDetails = {
                        orderId: statusResult.orderId,
                        merchantOrderId: statusResult.merchantOrderId,
                        amount: statusResult.amount,
                        payableAmount: statusResult.payableAmount,
                        feeAmount: statusResult.feeAmount,
                        paymentMode: statusResult.latestPayment?.paymentMode,
                        transactionId: statusResult.latestPayment?.transactionId,
                        timestamp: statusResult.latestPayment?.timestamp,
                        metaInfo: statusResult.metaInfo,
                        completedAt: new Date().toISOString()
                    };
                    
                    localStorage.setItem('payment_details', JSON.stringify(paymentDetails));
                    setShowSuccessModal(true);
                    
                } else if (statusResult.isFailed) {
                    // Payment failed
                    console.log('[DonationPage] Payment failed');
                    
                    let errorMessage = 'Payment failed. Please try again.';
                    
                    // Add specific error details if available
                    if (statusResult.errorContext) {
                        const errorContext = statusResult.errorContext;
                        errorMessage += `\n\nError Details:\n`;
                        errorMessage += `- Error Code: ${errorContext.errorCode || 'Unknown'}\n`;
                        errorMessage += `- Description: ${errorContext.description || 'No description available'}\n`;
                        errorMessage += `- Stage: ${errorContext.stage || 'Unknown'}`;
                    } else if (statusResult.latestPayment?.errorCode) {
                        errorMessage += `\n\nError Code: ${statusResult.latestPayment.errorCode}`;
                        if (statusResult.latestPayment.detailedErrorCode) {
                            errorMessage += ` (${statusResult.latestPayment.detailedErrorCode})`;
                        }
                    }
                    
                    setErrorMessage(errorMessage);
                    setShowErrorModal(true);
                    
                } else if (statusResult.isPending) {
                    // Payment still pending
                    console.log('[DonationPage] Payment is still pending');
                    
                    let pendingMessage = 'Payment is still being processed.';
                    
                    if (statusResult.hasPaymentAttempts) {
                        const latestAttempt = statusResult.latestPayment;
                        pendingMessage += `\n\nLatest attempt: ${latestAttempt.paymentMode} - ${latestAttempt.state}`;
                        if (latestAttempt.timestamp) {
                            const attemptTime = new Date(latestAttempt.timestamp).toLocaleString();
                            pendingMessage += `\nAttempt time: ${attemptTime}`;
                        }
                    }
                    
                    pendingMessage += '\n\nPlease check back in a few minutes or contact support if the issue persists.';
                    
                    setErrorMessage(pendingMessage);
                    setShowErrorModal(true);
                    
                } else {
                    // Unknown status
                    console.log('[DonationPage] Unknown payment status:', status);
                    setErrorMessage(`Payment status is unclear (${status}). Please contact support for assistance.`);
                    setShowErrorModal(true);
                }
            } else {
                throw new Error('Failed to check payment status');
            }

        } catch (error) {
            console.error('[DonationPage] Error checking payment status:', error);
            
            let errorMessage = 'Unable to verify payment status.';
            
            if (error.message.includes('INVALID_MERCHANT_ORDER_ID')) {
                errorMessage = 'Payment order not found. Please contact support if you have been charged.';
            } else if (error.message.includes('UNAUTHORIZED')) {
                errorMessage = 'Authentication failed. Please try again.';
            } else {
                errorMessage += ' Please contact support if you have been charged.';
            }
            
            setErrorMessage(errorMessage);
            setShowErrorModal(true);
        }
    };

    const closePhonePeIframe = () => {
        try {
            if (window.PhonePeCheckout && window.PhonePeCheckout.closePage) {
                window.PhonePeCheckout.closePage();
            }
            setShowPhonePeIframe(false);
        } catch (error) {
            console.error('[DonationPage] Error closing PhonePe iframe:', error);
            setShowPhonePeIframe(false);
        }
    };

    /**
     * Handle form submission and payment processing
     * Implements industry-standard payment workflow with proper validation
     */
    const handleDonationSubmit = async () => {
        // Validate form before processing
        if (!validateForm()) {
            console.warn('[DonationPage] Form validation failed');
            return;
        }
        
        // Validate payment method selection
        if (!paymentMethod) {
            alert('Please select a payment method');
            return;
        }
        
        setIsProcessing(true);
        
        try {
            // Handle PhonePe/UPI payment via PhonePe service
            if (paymentMethod === 'phonepe' || paymentMethod === 'upi') {
                console.log('[DonationPage] Processing PhonePe payment for', paymentMethod);
                await handlePhonePePayment();
                return;
            }
            
            // Handle other payment methods (simulated for now)
            console.log('[DonationPage] Processing other payment method:', paymentMethod);
            
            // Simulate payment processing for other methods
            setTimeout(() => {
                setIsProcessing(false);
                setShowPaymentModal(false);
                setShowSuccessModal(true);
            }, 3000);
            
        } catch (error) {
            console.error('[DonationPage] Payment processing error:', error);
            setIsProcessing(false);
            alert('Payment processing failed. Please try again.');
        }
    };

    /**
     * Get the final donation amount
     */
    const getFinalAmount = () => {
        return customAmount || donationAmount || 0;
    };

    /**
     * Handle social sharing after successful donation
     */
    const handleSocialShare = (platform) => {
        const message = `I just donated ₹${getFinalAmount()} to Ummeed Ki Kiran Foundation Trust to support ${selectedCause || 'their cause'}. Join me in making a difference!`;
        const url = window.location.href;
        
        let shareUrl = '';
        switch (platform) {
            case 'facebook':
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(message)}`;
                break;
            case 'twitter':
                shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(url)}`;
                break;
            case 'whatsapp':
                shareUrl = `https://wa.me/?text=${encodeURIComponent(message + ' ' + url)}`;
                break;
            default:
                break;
        }
        
        if (shareUrl) {
            window.open(shareUrl, '_blank', 'width=600,height=400');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Tab Navigation */}
            {/* Hero Section */}
            {/* Hero Section */}
            <motion.section
                className="relative py-20 bg-gradient-to-br from-red-600 via-pink-600 to-purple-700 text-white overflow-hidden"
                initial="hidden"
                animate="visible"
            >
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="relative z-10 max-w-6xl mx-auto px-4 text-center">
                    <motion.div variants={staggerContainer}>
                        <motion.div
                            className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-md rounded-full mb-8"
                            variants={scaleIn}
                        >
                            <FaHandHoldingHeart className="text-4xl text-white" />
                        </motion.div>
                        <motion.h1
                            className="text-5xl md:text-6xl font-bold mb-6"
                            variants={fadeInUp}
                        >
                            Support Our Cause
                        </motion.h1>
                        <motion.p
                            className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed"
                            variants={fadeInUp}
                        >
                            Your generous contribution empowers us to continue our work and bring positive change
                            in the lives of underprivileged children and communities.
                        </motion.p>
                        <motion.div
                            className="bg-white/10 backdrop-blur-md px-8 py-4 rounded-full inline-block"
                            variants={fadeInUp}
                        >
                            <p className="text-lg font-medium">
                                Every donation, no matter the size, makes a significant difference
                            </p>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.section>



            {/* Online Donation Form Section */}
            <motion.section
                className="py-20 bg-gray-50"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div className="text-center mb-16" variants={staggerContainer}>
                        <motion.h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
                            Donate Online - Secure & Instant
                        </motion.h2>
                        <motion.p className="text-xl text-gray-600 max-w-3xl mx-auto" variants={fadeInUp}>
                            Make a secure online donation with multiple payment options. Your contribution is processed instantly and securely.
                        </motion.p>
                        <motion.div className="flex items-center justify-center gap-4 mt-6" variants={fadeInUp}>
                            <div className="flex items-center gap-2 text-green-600">
                                <FaShieldAlt />
                                <span className="text-sm font-medium">SSL Secured</span>
                            </div>
                            <div className="flex items-center gap-2 text-blue-600">
                                <FaLock />
                                <span className="text-sm font-medium">PCI Compliant</span>
                            </div>
                            <div className="flex items-center gap-2 text-purple-600">
                                <FaUserShield />
                                <span className="text-sm font-medium">Data Protected</span>
                            </div>
                        </motion.div>
                    </motion.div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Donation Form */}
                        <motion.div
                            className="lg:col-span-2 bg-white rounded-2xl shadow-lg p-8"
                            variants={slideIn}
                        >
                            {/* Step 1: Amount Selection */}
                            <div className="mb-8">
                                <h3 className="text-2xl font-bold text-gray-800 mb-6">Choose Donation Amount</h3>
                                
                                {/* Preset Amounts */}
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                                    {presetAmounts.map((amount) => (
                                        <button
                                            key={amount}
                                            onClick={() => handleAmountSelect(amount)}
                                            className={`p-4 rounded-lg border-2 transition-all ${
                                                donationAmount === amount
                                                    ? 'border-blue-600 bg-blue-50 text-blue-600'
                                                    : 'border-gray-200 hover:border-blue-300'
                                            }`}
                                        >
                                            <div className="text-lg font-bold">₹{amount}</div>
                                        </button>
                                    ))}
                                </div>

                                {/* Custom Amount */}
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Or enter custom amount
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                                        <input
                                            type="number"
                                            value={customAmount}
                                            onChange={handleCustomAmountChange}
                                            placeholder="Enter amount"
                                            className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            min="1"
                                        />
                                    </div>
                                    {formErrors.amount && (
                                        <p className="text-red-500 text-sm mt-1">{formErrors.amount}</p>
                                    )}
                                </div>

                                {/* Donation Frequency */}
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Donation Frequency
                                    </label>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        {[
                                            { value: 'one-time', label: 'One Time' },
                                            { value: 'monthly', label: 'Monthly' },
                                            { value: 'quarterly', label: 'Quarterly' },
                                            { value: 'yearly', label: 'Yearly' }
                                        ].map((freq) => (
                                            <button
                                                key={freq.value}
                                                onClick={() => setDonationFrequency(freq.value)}
                                                className={`p-3 rounded-lg border-2 transition-all ${
                                                    donationFrequency === freq.value
                                                        ? 'border-green-600 bg-green-50 text-green-600'
                                                        : 'border-gray-200 hover:border-green-300'
                                                }`}
                                            >
                                                <div className="text-sm font-medium">{freq.label}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Cause Selection */}
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Choose a Cause (Optional)
                                    </label>
                                    <select
                                        value={selectedCause}
                                        onChange={(e) => setSelectedCause(e.target.value)}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">Select a cause</option>
                                        {donationCauses.map((cause) => (
                                            <option key={cause.value} value={cause.value}>
                                                {cause.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Step 2: Donor Information */}
                            <div className="mb-8 border-t pt-8">
                                <h3 className="text-2xl font-bold text-gray-800 mb-6">Donor Information</h3>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Full Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={donorInfo.name}
                                            onChange={(e) => handleDonorInfoChange('name', e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Enter your full name"
                                        />
                                        {formErrors.name && (
                                            <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
                                        )}
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Email Address *
                                        </label>
                                        <input
                                            type="email"
                                            value={donorInfo.email}
                                            onChange={(e) => handleDonorInfoChange('email', e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Enter your email"
                                        />
                                        {formErrors.email && (
                                            <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
                                        )}
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Phone Number *
                                        </label>
                                        <input
                                            type="tel"
                                            value={donorInfo.phone}
                                            onChange={(e) => handleDonorInfoChange('phone', e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="9876543210 or 0123456789 or +919876543210"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Enter your 10-digit mobile number (supports formats: 9876543210, 0123456789, +919876543210)
                                        </p>
                                        {formErrors.phone && (
                                            <p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>
                                        )}
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            City
                                        </label>
                                        <input
                                            type="text"
                                            value={donorInfo.city}
                                            onChange={(e) => handleDonorInfoChange('city', e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Enter your city"
                                        />
                                    </div>
                                </div>

                                {/* Tax Certificate Option */}
                                <div className="mt-6">
                                    <label className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            checked={donorInfo.needTaxCertificate}
                                            onChange={(e) => handleDonorInfoChange('needTaxCertificate', e.target.checked)}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">
                                            I need a tax exemption certificate (80G)
                                        </span>
                                    </label>
                                    
                                    {donorInfo.needTaxCertificate && (
                                        <div className="mt-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                PAN Number *
                                            </label>
                                            <input
                                                type="text"
                                                value={donorInfo.panNumber}
                                                onChange={(e) => handleDonorInfoChange('panNumber', e.target.value.toUpperCase())}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Enter your PAN number"
                                                maxLength="10"
                                            />
                                            {formErrors.panNumber && (
                                                <p className="text-red-500 text-sm mt-1">{formErrors.panNumber}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Payment Method - PhonePe Only */}
                            <div className="mb-8 border-t pt-8">
                                <h3 className="text-2xl font-bold text-gray-800 mb-6">Payment Method</h3>
                                
                                <div className="relative">
                                    <div className="p-6 rounded-xl border-2 border-purple-600 bg-gradient-to-r from-purple-50 to-pink-50">
                                        <div className="flex items-center gap-4">
                                            <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center text-white text-3xl">
                                                <FaMobile />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h4 className="text-xl font-bold text-purple-800">Pay via PhonePe</h4>
                                                    <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                                                        RECOMMENDED
                                                    </span>
                                                </div>
                                                <p className="text-gray-700 mb-2">UPI, Credit/Debit Cards, Wallets & More</p>
                                                <div className="flex items-center gap-4 text-sm">
                                                    <div className="flex items-center gap-1 text-green-600">
                                                        <FaShieldAlt />
                                                        <span className="font-medium">100% Secure</span>
                                                    </div>
                                                    <div className="flex items-center gap-1 text-blue-600">
                                                        <FaCheckCircle />
                                                        <span className="font-medium">Instant Confirmation</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Donate Button */}
                            <button
                                onClick={() => setShowPaymentModal(true)}
                                disabled={!getFinalAmount() || !paymentMethod}
                                className="w-full bg-gradient-to-r from-red-600 to-pink-600 text-white py-4 px-8 rounded-lg font-bold text-lg hover:from-red-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Donate ₹{getFinalAmount()} {donationFrequency !== 'one-time' && `(${donationFrequency})`}
                            </button>
                        </motion.div>

                        {/* Sidebar */}
                        <motion.div className="space-y-6" variants={slideIn}>
                            {/* Impact Calculator */}
                            {getFinalAmount() > 0 && (
                                <div className="bg-white rounded-2xl shadow-lg p-6">
                                    <h4 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                                        <FaChartLine className="text-green-600" />
                                        Your Impact
                                    </h4>
                                    <div className="space-y-3">
                                        {calculateImpact(getFinalAmount()).map((impact, index) => (
                                            <div key={index} className="flex items-start gap-3">
                                                <FaCheckCircle className="text-green-600 mt-1 flex-shrink-0" />
                                                <p className="text-sm text-gray-700">{impact}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Recent Donors */}
                            {/*
                            <div className="bg-white rounded-2xl shadow-lg p-6">
                                <h4 className="text-xl font-bold text-gray-800 mb-4">Recent Donors</h4>
                                <div className="space-y-3">
                                    {recentDonors.map((donor, index) => (
                                        <div key={index} className="flex items-center justify-between">
                                            <div>
                                                <div className="font-medium text-gray-800">{donor.name}</div>
                                                <div className="text-sm text-gray-600">{donor.cause} • {donor.time}</div>
                                            </div>
                                            <div className="text-green-600 font-bold">₹{donor.amount}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            */}

                            {/* Security Badges */}
                            <div className="bg-white rounded-2xl shadow-lg p-6">
                                <h4 className="text-xl font-bold text-gray-800 mb-4">Security & Trust</h4>
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3">
                                        <FaShieldAlt className="text-green-600" />
                                        <span className="text-sm text-gray-700">256-bit SSL Encryption</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FaLock className="text-blue-600" />
                                        <span className="text-sm text-gray-700">PCI DSS Compliant</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <FaUserShield className="text-purple-600" />
                                        <span className="text-sm text-gray-700">Data Protection Certified</span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </motion.section>

            {/* Donation Impact Section */}
            <motion.section
                className="py-20 bg-white"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div className="text-center mb-16" variants={staggerContainer}>
                        <motion.h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
                            How Your Donation Helps
                        </motion.h2>
                        <motion.p className="text-xl text-gray-600 max-w-3xl mx-auto" variants={fadeInUp}>
                            See the direct impact your contribution can make in transforming lives and building stronger communities
                        </motion.p>
                    </motion.div>

                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
                        variants={staggerContainer}
                    >
                        {donationImpacts.map((impact, index) => (
                            <motion.div
                                key={index}
                                className="bg-white rounded-2xl shadow-lg p-8 text-center group border-2 border-transparent hover:border-gray-200"
                                variants={scaleIn}
                                whileHover={{ y: -10, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl group-hover:scale-110 transition-transform ${impact.color === 'blue' ? 'bg-blue-600' :
                                    impact.color === 'red' ? 'bg-red-600' :
                                        impact.color === 'green' ? 'bg-green-600' :
                                            impact.color === 'purple' ? 'bg-purple-600' : 'bg-gray-600'
                                    }`}>
                                    {impact.icon}
                                </div>
                                <h3 className="text-3xl font-bold text-gray-800 mb-4">{impact.amount}</h3>
                                <p className="text-gray-600 leading-relaxed">{impact.description}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </motion.section>

            {/* Testimonials Section */}
            <motion.section
                className="py-20 bg-gray-50"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="max-w-6xl mx-auto px-4">
                    <motion.div className="text-center mb-16" variants={staggerContainer}>
                        <motion.h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
                            Stories of Impact
                        </motion.h2>
                        <motion.p className="text-xl text-gray-600 max-w-3xl mx-auto" variants={fadeInUp}>
                            Hear from the people whose lives have been transformed by your generous support
                        </motion.p>
                    </motion.div>

                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 gap-8"
                        variants={staggerContainer}
                    >
                        {testimonials.map((testimonial, index) => (
                            <motion.div
                                key={index}
                                className="bg-white rounded-2xl shadow-lg p-8"
                                variants={scaleIn}
                            >
                                <div className="flex items-center gap-1 mb-4">
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                        <FaStar key={i} className="text-yellow-400" />
                                    ))}
                                </div>
                                <p className="text-gray-700 mb-6 italic">"{testimonial.message}"</p>
                                <div>
                                    <div className="font-bold text-gray-800">{testimonial.name}</div>
                                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </motion.section>

            {/* Traditional Donation Methods Section */}
            <motion.section
                className="py-20 bg-white"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div className="text-center mb-16" variants={staggerContainer}>
                        <motion.h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
                            Other Ways to Donate
                        </motion.h2>
                        <motion.p className="text-xl text-gray-600 max-w-3xl mx-auto" variants={fadeInUp}>
                            Prefer traditional methods? We also accept donations through bank transfers and cheques.
                        </motion.p>
                    </motion.div>

                    <motion.div
                        className="grid grid-cols-1 lg:grid-cols-2 gap-8"
                        variants={staggerContainer}
                    >
                        {/* Bank Transfer */}
                        <motion.div
                            className="bg-white rounded-2xl shadow-lg p-8 group"
                            variants={scaleIn}
                            whileHover={{ y: -5, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl group-hover:scale-110 transition-transform">
                                    <FaUniversity />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-gray-800">Bank Transfer</h3>
                                    <p className="text-blue-600 font-medium">Secure & Direct (Within India)</p>
                                </div>
                            </div>
                            <p className="text-gray-600 mb-6 leading-relaxed">
                                Make a direct bank transfer for immediate processing of your donation.
                                Contact us for detailed bank account information.
                            </p>
                            <div className="space-y-4">
                                <div className="flex items-center gap-3 text-gray-700">
                                    <FaEnvelope className="text-blue-600" />
                                    <a href="mailto:support@ummeedkikiran.org" className="hover:text-blue-600 transition-colors">
                                        support@ummeedkikiran.org
                                    </a>
                                </div>
                                <div className="flex items-center gap-3 text-gray-700">
                                    <FaPhone className="text-blue-600" />
                                    <a href="tel:+917985947303" className="hover:text-blue-600 transition-colors">
                                        +917985947303
                                    </a>
                                </div>
                            </div>
                        </motion.div>

                        {/* Cheque/DD */}
                        <motion.div
                            className="bg-white rounded-2xl shadow-lg p-8 group"
                            variants={scaleIn}
                            whileHover={{ y: -5, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center text-white text-2xl group-hover:scale-110 transition-transform">
                                    <FaMoneyBillWave />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-gray-800">Cheque / Demand Draft</h3>
                                    <p className="text-green-600 font-medium">Traditional & Reliable</p>
                                </div>
                            </div>
                            <p className="text-gray-600 mb-6 leading-relaxed">
                                Send a cheque or demand draft in favor of <strong>"Ummeed Ki Kiran Foundation Trust"</strong> to our postal address.
                            </p>
                            <div className="bg-gray-50 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <FaMapMarkerAlt className="text-green-600 mt-1" />
                                    <div>
                                        <p className="font-semibold text-gray-800 mb-2">Postal Address:</p>
                                        <p className="text-gray-700">
                                            S 17/314 D-15, Charan Nagar Colony<br />
                                            Nadesar, Uttar Pradesh, India
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.section>

            {/* Contact & Support Section */}
            <motion.section
                className="py-20 bg-gradient-to-br from-gray-900 to-blue-900 text-white"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                <div className="max-w-6xl mx-auto px-4 text-center">
                    <motion.div variants={staggerContainer}>
                        <motion.div
                            className="w-20 h-20 bg-gradient-to-br from-red-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-8"
                            variants={scaleIn}
                        >
                            <FaHeart className="text-3xl text-white" />
                        </motion.div>
                        <motion.h3 className="text-4xl font-bold mb-6" variants={fadeInUp}>
                            Thank You for Your Support!
                        </motion.h3>
                        <motion.p className="text-xl mb-8 max-w-3xl mx-auto leading-relaxed opacity-90" variants={fadeInUp}>
                            For any queries regarding donations, or if you wish to support us in other ways
                            (e.g., in-kind donations, volunteering), please feel free to reach out to us.
                        </motion.p>
                        <motion.div
                            className="bg-yellow-400/20 border border-yellow-400/30 rounded-lg p-6 max-w-2xl mx-auto"
                            variants={fadeInUp}
                        >
                            <p className="text-yellow-200 font-medium text-lg">
                                🌟 Your support is invaluable. Thank you for being a ray of hope! 🌟
                            </p>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.section>

            {/* Payment Processing Modal */}
            <AnimatePresence>
                {showPaymentModal && (
                    <motion.div
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="bg-white rounded-2xl p-8 max-w-md w-full"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                        >
                            <div className="text-center">
                                <div className={`w-16 h-16 ${(paymentMethod === 'phonepe' || paymentMethod === 'upi') ? 'bg-purple-600' : 'bg-blue-600'} rounded-full flex items-center justify-center mx-auto mb-6`}>
                                    {(paymentMethod === 'phonepe' || paymentMethod === 'upi') ? (
                                        <FaMobile className="text-2xl text-white" />
                                    ) : (
                                        <FaCreditCard className="text-2xl text-white" />
                                    )}
                                </div>
                                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                                    {(paymentMethod === 'phonepe' || paymentMethod === 'upi') ? 'UPI via PhonePe' : 'Confirm Your Donation'}
                                </h3>
                                {(paymentMethod === 'phonepe' || paymentMethod === 'upi') && (
                                    <>
                                        {!phonepeConfig.isValid ? (
                                            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                                                <div className="flex items-center justify-center gap-2 text-red-700">
                                                    <FaTimes className="text-sm" />
                                                    <span className="text-sm font-medium">PhonePe Configuration Error</span>
                                                </div>
                                                <p className="text-xs text-red-600 mt-1 text-center">
                                                    Please contact administrator to fix this issue
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                                                <div className="flex items-center justify-center gap-2 text-purple-700">
                                                    <FaShieldAlt className="text-sm" />
                                                    <span className="text-sm font-medium">Secured by PhonePe</span>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                                <div className={`${(paymentMethod === 'phonepe' || paymentMethod === 'upi') ? 'bg-purple-50' : 'bg-gray-50'} rounded-lg p-4 mb-6`}>
                                    <div className={`text-3xl font-bold ${(paymentMethod === 'phonepe' || paymentMethod === 'upi') ? 'text-purple-600' : 'text-blue-600'} mb-2`}>₹{getFinalAmount()}</div>
                                    <div className="text-sm text-gray-600">
                                        {donationFrequency !== 'one-time' && `${donationFrequency} donation`}
                                        {selectedCause && ` • ${donationCauses.find(c => c.value === selectedCause)?.label}`}
                                    </div>
                                    {(paymentMethod === 'phonepe' || paymentMethod === 'upi') && (
                                        <div className="text-xs text-purple-600 mt-2">
                                            Payment via PhonePe Gateway
                                        </div>
                                    )}
                                </div>
                                
                                {!isProcessing ? (
                                    <div className="space-y-4">
                                        <button
                                            onClick={handleDonationSubmit}
                                            disabled={(paymentMethod === 'phonepe' || paymentMethod === 'upi') && !phonepeConfig.isValid}
                                            className={`w-full ${
                                                (paymentMethod === 'phonepe' || paymentMethod === 'upi') 
                                                    ? (!phonepeConfig.isValid 
                                                        ? 'bg-gray-400 cursor-not-allowed' 
                                                        : 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800')
                                                    : 'bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700'
                                            } text-white py-3 px-6 rounded-lg font-bold transition-all`}
                                        >
                                            {(paymentMethod === 'phonepe' || paymentMethod === 'upi') 
                                                ? (!phonepeConfig.isValid ? 'PhonePe Not Available' : 'Pay with PhonePe') 
                                                : 'Proceed to Payment'}
                                        </button>
                                        <button
                                            onClick={() => setShowPaymentModal(false)}
                                            className="w-full bg-gray-200 text-gray-800 py-3 px-6 rounded-lg font-medium hover:bg-gray-300 transition-all"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-center">
                                            <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${(paymentMethod === 'phonepe' || paymentMethod === 'upi') ? 'border-purple-600' : 'border-blue-600'}`}></div>
                                        </div>
                                        <p className="text-gray-600">
                                            {(paymentMethod === 'phonepe' || paymentMethod === 'upi') ? 'Connecting to PhonePe...' : 'Processing your payment...'}
                                        </p>
                                        <p className="text-sm text-gray-500">Please do not close this window</p>
                                        {(paymentMethod === 'phonepe' || paymentMethod === 'upi') && (
                                            <p className="text-xs text-purple-600">You will be redirected to PhonePe for secure payment</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Success Modal */}
            <AnimatePresence>
                {showSuccessModal && (
                    <motion.div
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="bg-white rounded-2xl p-8 max-w-md w-full"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                        >
                            <div className="text-center">
                                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <FaCheckCircle className="text-2xl text-white" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-800 mb-4">Thank You!</h3>
                                <p className="text-gray-600 mb-6">
                                    Your donation of ₹{getFinalAmount()} has been processed successfully.
                                    You will receive a confirmation email shortly.
                                </p>
                                
                                <div className="space-y-4">
                                    <button
                                        onClick={() => {/* Download receipt logic */}}
                                        className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                                    >
                                        <FaDownload />
                                        Download Receipt
                                    </button>
                                    
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleSocialShare('facebook')}
                                            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg text-sm hover:bg-blue-700 transition-all"
                                        >
                                            Share on Facebook
                                        </button>
                                        <button
                                            onClick={() => handleSocialShare('twitter')}
                                            className="flex-1 bg-blue-400 text-white py-2 px-4 rounded-lg text-sm hover:bg-blue-500 transition-all"
                                        >
                                            Share on Twitter
                                        </button>
                                        <button
                                            onClick={() => handleSocialShare('whatsapp')}
                                            className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg text-sm hover:bg-green-700 transition-all"
                                        >
                                            WhatsApp
                                        </button>
                                    </div>
                                    
                                    <button
                                        onClick={() => {
                                            setShowSuccessModal(false);
                                            // Reset form
                                            setDonationAmount('');
                                            setCustomAmount('');
                                            setPaymentMethod('phonepe');
                                            setDonorInfo({
                                                name: '',
                                                email: '',
                                                phone: '',
                                                address: '',
                                                city: '',
                                                state: '',
                                                pincode: '',
                                                panNumber: '',
                                                needTaxCertificate: false
                                            });
                                        }}
                                        className="w-full bg-gray-200 text-gray-800 py-3 px-6 rounded-lg font-medium hover:bg-gray-300 transition-all"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Error Modal */}
            <AnimatePresence>
                {showErrorModal && (
                    <motion.div
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowErrorModal(false)}
                    >
                        <motion.div
                            className="bg-white rounded-2xl p-8 max-w-md w-full"
                            initial={{ scale: 0.8, opacity: 0, y: 50 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.8, opacity: 0, y: 50 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="text-center">
                                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <FaTimes className="text-3xl text-red-600" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-800 mb-4">Payment Failed</h3>
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                                    <p className="text-red-800 text-sm leading-relaxed">
                                        {errorMessage}
                                    </p>
                                </div>
                                
                                <div className="space-y-3">
                                    <button
                                        onClick={() => {
                                            setShowErrorModal(false);
                                            setErrorMessage('');
                                            setShowPaymentModal(true);
                                        }}
                                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-lg font-bold hover:from-purple-700 hover:to-pink-700 transition-all"
                                    >
                                        Try Again
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowErrorModal(false);
                                            setErrorMessage('');
                                        }}
                                        className="w-full bg-gray-200 text-gray-800 py-3 px-6 rounded-lg font-medium hover:bg-gray-300 transition-all"
                                    >
                                        Close
                                    </button>
                                </div>

                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <p className="text-sm text-gray-600 mb-2">Need help?</p>
                                    <div className="flex items-center justify-center gap-4 text-sm">
                                        <a href="mailto:support@ummeedkikiran.org" className="text-purple-600 hover:text-purple-700 font-medium">
                                            Email Support
                                        </a>
                                        <span className="text-gray-400">|</span>
                                        <a href="tel:+917985947303" className="text-purple-600 hover:text-purple-700 font-medium">
                                            Call Us
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* PhonePe Iframe Overlay */}
            <AnimatePresence>
                {showPhonePeIframe && (
                    <motion.div
                        className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="bg-white rounded-2xl p-6 max-w-4xl w-full h-[90vh] flex flex-col"
                            initial={{ scale: 0.9, opacity: 0, y: 50 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 50 }}
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
                                        <FaCreditCard className="text-white text-lg" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-800">Secure Payment</h3>
                                        <p className="text-sm text-gray-600">Powered by PhonePe</p>
                                    </div>
                                </div>
                                <button
                                    onClick={closePhonePeIframe}
                                    className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                                >
                                    <FaTimes className="text-gray-600 text-sm" />
                                </button>
                            </div>

                            {/* Payment Info */}
                            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 mb-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-600">Donation Amount</p>
                                        <p className="text-2xl font-bold text-gray-800">₹{getFinalAmount()}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm text-gray-600">Cause</p>
                                        <p className="font-medium text-gray-800">{selectedCause || 'General Donation'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* PhonePe Iframe Container */}
                            <div className="flex-1 bg-gray-50 rounded-lg overflow-hidden">
                                <div className="h-full flex items-center justify-center">
                                    <div className="text-center">
                                        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                                        </div>
                                        <h4 className="text-lg font-semibold text-gray-800 mb-2">Loading Payment Page</h4>
                                        <p className="text-gray-600 mb-4">Please wait while we connect you to PhonePe's secure payment gateway...</p>
                                        <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                                            <FaShieldAlt className="text-green-600" />
                                            <span>Your payment is secure and encrypted</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="mt-6 pt-4 border-t border-gray-200">
                                <div className="flex items-center justify-between text-sm text-gray-600">
                                    <div className="flex items-center gap-4">
                                        <div className="flex items-center gap-1">
                                            <FaLock className="text-green-600" />
                                            <span>SSL Secured</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <FaShieldAlt className="text-blue-600" />
                                            <span>PCI Compliant</span>
                                        </div>
                                    </div>
                                    <p className="text-xs">Powered by PhonePe</p>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default DonationPage;